import Controller from './controller'
import { clamp, scrollToPosition } from '../utilities'

export default class extends Controller {
  static targets = ['track', 'previous', 'next', 'thumbs']

  static options = {
    dragThreshold: 3,
    minSwipeDistance: 50,
  }

  initialize() {
    this.checkPosition = this.checkPosition.bind(this)

    this.slides = Array.from(this.trackTarget.children)

    if (this.hasThumbsTarget) {
      this.thumbs()
    }

    this.resize()
    this.checkPosition()
    this.setUpDragging()

    this.trackTarget.addEventListener('scroll', this.checkPosition, { passive: true })
  }

  thumbs() {
    for (let index = 0; index < this.slides.length; index++) {
      const span = document.createElement('span')

      if (index == 0) {
        span.classList.add('is-selected')
      }

      this.thumbsTarget.appendChild(span)
    }

    this.thumbIcons = this.thumbsTarget.getElementsByTagName('span')
  }

  previous() {
    if (this.selectedSlideIndex === 0) {
      this.select(0)
    } else {
      this.select(this.selectedSlideIndex - this.slideCount)
    }
  }

  next() {
    if (this.selectedSlideIndex === this.slides.length - 1) {
      this.select(this.slides.length - 1)
    } else {
      this.select(this.selectedSlideIndex + this.slideCount)
    }
  }

  select(index) {
    index = clamp(index, 0, this.slides.length - 1)

    const left = this.slidePositions[index].left - this.slidePositions[0].left

    scrollToPosition({
      target: this.trackTarget,
      left,
    })
  }

  resize() {
    this.elementPosition = this.element.getBoundingClientRect()
    this.trackScrollWidth = this.trackTarget.scrollWidth
    this.trackWidth = this.trackTarget.getBoundingClientRect().width

    const scrollLeft = this.trackTarget.scrollLeft

    this.slidePositions = this.slides.map((slide) => {
      const rect = slide.getBoundingClientRect()

      return {
        left: rect.left + scrollLeft,
        width: rect.width,
      }
    })

    // Check how many slides fit on screen
    const slideWidth = this.slidePositions.length ? this.slidePositions[0].width : 0
    const slideGap = this.slidePositions.length > 1 ? this.slidePositions[1].left - (this.slidePositions[0].left + this.slidePositions[0].width) : 0
    this.slideCount = 1
    let fits = true
    let count = 1
    while (fits) {
      count += 1

      if (this.trackWidth >= (slideWidth * count) + (slideGap * count - 1)) {
        this.slideCount = count
      } else {
        fits = false
      }
    }

    this.checkPosition()
  }

  checkPosition() {
    const scrollLeft = this.trackTarget.scrollLeft
    const atStart = scrollLeft <= 0
    const atEnd = scrollLeft >= this.trackScrollWidth - this.trackWidth - 1

    // Check visible width of each slide on screen
    const visibleSlideWidths = this.slidePositions.map((position) => {
      const left = Math.max(position.left - scrollLeft, this.elementPosition.left)
      const right = Math.min(
        position.left - scrollLeft + position.width,
        this.elementPosition.right,
      )

      return Math.round(Math.max(right - left, 0))
    })

    // Select most visible slide
    const selectedSlideIndex = visibleSlideWidths.indexOf(Math.max(...visibleSlideWidths))

    if (atStart !== this.atStart) {
      this.previousTarget.disabled = atStart
      this.atStart = atStart
    }

    if (atEnd !== this.atEnd) {
      this.nextTarget.disabled = atEnd
      this.atEnd = atEnd
    }

    if (selectedSlideIndex === this.selectedSlideIndex) {
      return
    }

    if (this.selectedSlideIndex != null) {
      this.slides[this.selectedSlideIndex].classList.remove('is-selected')
    }

    this.slides[selectedSlideIndex].classList.add('is-selected')

    if (this.hasThumbsTarget) {
      for (let index = 0; index < this.thumbIcons.length; index++) {
        this.thumbIcons[index].classList.remove('is-selected')
      }
      this.thumbIcons[selectedSlideIndex].classList.add('is-selected')
    }

    this.selectedSlideIndex = selectedSlideIndex
  }

  setUpDragging() {
    this.mouseDown = this.mouseDown.bind(this)
    this.mouseMove = this.mouseMove.bind(this)
    this.mouseUp = this.mouseUp.bind(this)

    this.trackTarget.addEventListener('mousedown', this.mouseDown)
    this.trackTarget.addEventListener('dragstart', (ev) => {
      // Prevent dragging of links etc. within slide content
      ev.preventDefault()
    })
  }

  mouseDown(ev) {
    this.dragStartX = ev.screenX

    this.trackTarget.classList.add('is-mouse-down')

    window.addEventListener('mousemove', this.mouseMove)
    window.addEventListener('mouseup', this.mouseUp)
  }

  mouseMove(ev) {
    if (this.isDragStarted) {
      this.drag(ev)
    } else if (Math.abs(this.dragStartX - ev.screenX) > this.options.dragThreshold) {
      this.dragStart(ev)
    }
  }

  mouseUp(ev) {
    if (this.isDragStarted) {
      this.dragEnd(ev)
    }

    this.trackTarget.classList.remove('is-mouse-down')

    window.removeEventListener('mousemove', this.mouseMove)
    window.removeEventListener('mouseup', this.mouseUp)
  }

  dragStart(ev) {
    this.isDragStarted = true
    this.scrollLeftStart = this.trackTarget.scrollLeft
    this.selectedSlideIndexOnDragStart = this.selectedSlideIndex

    clearInterval(this.checkIfScrollIsStatic)

    this.trackTarget.classList.add('is-dragging')

    this.drag(ev)
  }

  drag(ev) {
    this.trackTarget.scroll({
      left: this.scrollLeftStart + (this.dragStartX - ev.screenX),
      behavior: 'instant',
    })
  }

  dragEnd(ev) {
    if (this.selectedSlideIndex !== this.selectedSlideIndexOnDragStart) {
      // If a new slide has been dragged into the selected position, snap to that slide
      this.select(this.selectedSlideIndex)
    } else if (this.dragStartX - ev.screenX > this.options.minSwipeDistance) {
      // If the minimum forward swipe distance has been reached, snap to the next slide
      this.next()
    } else if (ev.screenX - this.dragStartX > this.options.minSwipeDistance) {
      // If the minimum backward swipe distance has been reached, snap to the previous slide
      this.previous()
    } else {
      // Otherwise, snap back to the current slide
      this.select(this.selectedSlideIndexOnDragStart)
    }

    this.isDragStarted = false

    // Detect when smooth scroll from `scrollToPosition` ends, and restore scroll snapping at that point
    // (via CSS, by removing `.is-dragging` from `.slider__track`). Restoring scroll snapping before the
    // smooth scrolling ends will cause the slider to jump instantly to the snap point
    let position = null
    this.checkIfScrollIsStatic = setInterval(() => {
      if (position === this.trackTarget.scrollLeft) {
        this.trackTarget.classList.remove('is-dragging')
        clearInterval(this.checkIfScrollIsStatic)
      }
      position = this.trackTarget.scrollLeft
    }, 50)
  }
}
